
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-beige via-white to-accent-light-sage"></div>

      {/* Fixed background image string – removed template literal backticks which caused a syntax error */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'url("https://public.readdy.ai/ai/img_res/c4f00b37901ea0cba9c5eaab0861b537.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full border border-primary-gold shadow-sm"
            >
              <i className="ri-star-fill text-primary-gold text-sm"></i>
              <span className="text-sm font-medium text-primary-charcoal">
                Trusted by 200+ Entrepreneurs
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="font-serif font-bold text-5xl lg:text-7xl text-primary-charcoal leading-tight"
            >
              Transform Your{' '}
              <span className="text-primary-gold">Business</span>, E-commerce &{' '}
              <span className="text-primary-sage">Wellbeing</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-lg lg:text-xl text-gray-600 leading-relaxed"
            >
              Expert consulting and coaching to help you grow profitably, sell
              smarter, and live healthier.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-gold hover:bg-accent-warm-gold text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Book a Free Consultation
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-primary-charcoal text-primary-charcoal hover:bg-primary-charcoal hover:text-white font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Explore Services
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="flex items-center space-x-6 text-sm text-gray-600"
            >
              <div className="flex items-center space-x-2">
                <i className="ri-checkbox-circle-fill text-primary-sage"></i>
                <span>5+ Years Experience</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-checkbox-circle-fill text-primary-sage"></i>
                <span>Personalized Strategies</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-checkbox-circle-fill text-primary-sage"></i>
                <span>Proven Results</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-[40px] overflow-hidden shadow-2xl">
              <div className="w-full h-[600px]">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20African%20business%20woman%20in%20her%20thirties%20with%20warm%20smile%20wearing%20elegant%20business%20casual%20attire%2C%20confident%20and%20approachable%20expression%2C%20standing%20in%20modern%20minimalist%20office%20with%20natural%20lighting%2C%20soft%20beige%20and%20sage%20green%20background%2C%20professional%20headshot%20style%20portrait%20perfect%20for%20business%20consulting%20and%20wellness%20coaching%20brand%2C%20high%20quality%20photography%20with%20shallow%20depth%20of%20field&width=600&height=800&seq=hero-portrait-1&orientation=portrait"
                  alt="Titilayo Ajayi - Business Consultant and Wellness Coach"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="absolute bottom-8 left-8 bg-white rounded-2xl shadow-xl p-6 backdrop-blur-sm"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-sage rounded-full">
                  <i className="ri-line-chart-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-charcoal">
                    500+
                  </div>
                  <div className="text-sm text-gray-600">Hours Coached</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
